import { formatAmount } from "@/lib/utils/formatters";
import {
  formatAddressDisplay,
  hasAddressForMapQuery,
} from "@/features/applicant/utils/addressHelpers";
import {
  formatDateMMDDYY,
  formatPhoneNumber,
} from "../../../utils/applicationFormatters";
import {
  showApplicationAddressReadOnlyField,
  showApplicationAddressReadOnlyScalar,
} from "../../../utils/addressReadOnlyVisibility";
import styles from "../../../styles/applicationDetail.module.css";
import { ADDRESS_TYPES } from "../aboutYourselfTabHelpers";
import type { AddressViewProps } from "../../../types/tabs";

export function AddressView({
  addressData,
  addressType,
  canValidateEmail = false,
  canViewAddressOnMap = false,
  onVerifyEmail,
  verifyingEmail = false,
  testIdPrefix,
}: AddressViewProps) {
  /** Matches Angular `#addressView` wrapper `*ngIf="addressData?.address_type"`. */
  if (!addressData?.address_type) {
    return null;
  }

  const mode =
    addressType === "previous_address" ? "previous_address" : "current_address";

  const addressTypeLabel = ADDRESS_TYPES[addressData.address_type] || "";
  const ad = addressData.address_data ?? {};

  const addressLineFormatted = formatAddressDisplay(addressData).trim();
  const moveInDisplay = formatDateMMDDYY(ad.moveIn_date ?? "");
  const moveOutDisplay = formatDateMMDDYY(ad.moveOut_date ?? "");
  const phoneDisplay = formatPhoneNumber(ad.landlord_mobile_no ?? "").trim();

  const living =
    ad.currently_living === true || ad.currently_living === "1";

  const showAddr = showApplicationAddressReadOnlyField(
    mode,
    addressLineFormatted,
  );
  const showType = showApplicationAddressReadOnlyField(mode, addressTypeLabel);

  const showMoveIn = showApplicationAddressReadOnlyField(mode, moveInDisplay);
  const showPlannedMoveOut =
    addressType === "current_address" &&
    showApplicationAddressReadOnlyField(mode, moveOutDisplay);
  const showStillHere = addressType === "current_address" && living;

  const landlordPrefix =
    addressData.address_type === "FAMILY" ? "Contact" : "Landlord";

  const showLandlordGrid =
    addressData.address_type !== "OWN" &&
    (mode === "previous_address" ||
      showApplicationAddressReadOnlyScalar(mode, ad.landlord_name) ||
      showApplicationAddressReadOnlyScalar(mode, ad.landlord_email) ||
      showApplicationAddressReadOnlyField(mode, phoneDisplay) ||
      (addressData.address_type === "FAMILY" &&
        showApplicationAddressReadOnlyScalar(
          mode,
          ad.landlord_relationship_type,
        )));

  const hasOwnRentPrimary =
    addressData.address_type === "OWN"
      ? showApplicationAddressReadOnlyScalar(mode, ad.month_reside) ||
        (addressType === "current_address" &&
          showApplicationAddressReadOnlyScalar(mode, ad.rent_amount))
      : showApplicationAddressReadOnlyScalar(mode, ad.paid_rent) ||
        showApplicationAddressReadOnlyScalar(mode, ad.rent_amount);

  const showMiddleGrid =
    hasOwnRentPrimary ||
    showMoveIn ||
    showStillHere ||
    showPlannedMoveOut;

  return (
    <div>
      {(showAddr || showType || showMiddleGrid) && (
        <div className={`${styles.balancedAddressGrid} grid grid-cols-12 gap-6 mb-6`}>
          {showAddr && (
            <div className="col-span-12 md:col-span-6 xl:col-span-5">
              <label className="block text-[11px] text-[#5e6e82] uppercase tracking-wide font-medium mb-1" data-testid={`${testIdPrefix}-address-label`}>
                Address
              </label>
              <div className="text-[13px] font-bold text-[#2d3e50]" data-testid={`${testIdPrefix}-address-value`}>
                {formatAddressDisplay(addressData)}
              </div>
              {addressType === "current_address" &&
                canViewAddressOnMap &&
                hasAddressForMapQuery(addressData) && (
                  <div className="mt-1">
                    <a
                      href={`https://www.google.com/maps/?q=${encodeURIComponent(formatAddressDisplay(addressData))}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#1597ff] hover:text-[#0d7acc] text-sm underline"
                      data-testid={`${testIdPrefix}-view-on-map-link`}
                    >
                      View on map
                    </a>
                  </div>
                )}
            </div>
          )}
          {showType && (
            <div className="col-span-12 md:col-span-3 xl:col-span-2">
              <label className="block text-[11px] text-[#5e6e82] uppercase tracking-wide font-medium mb-1" data-testid={`${testIdPrefix}-type-label`}>
                Type
              </label>
              <div className="text-[13px] font-bold text-[#2d3e50]" data-testid={`${testIdPrefix}-type-value`}>
                {addressTypeLabel}
              </div>
            </div>
          )}
          {showMiddleGrid && (
            <>
          {addressData.address_type === "OWN" ? (
            <>
              {showApplicationAddressReadOnlyScalar(mode, ad.month_reside) && (
                <div className="col-span-12 md:col-span-4 xl:col-span-3">
                  <label className="block text-[11px] text-[#5e6e82] uppercase tracking-wide font-medium mb-1" data-testid={`${testIdPrefix}-months-resided-label`}>
                    Number of months you resided at this address?
                  </label>
                  <div className="text-[13px] font-bold text-[#2d3e50]" data-testid={`${testIdPrefix}-months-resided-value`}>
                    {ad.month_reside}
                  </div>
                </div>
              )}
              {addressType === "current_address" &&
                showApplicationAddressReadOnlyScalar(mode, ad.rent_amount) && (
                  <div className="col-span-12 md:col-span-4 xl:col-span-2">
                    <label className="block text-[11px] text-[#5e6e82] uppercase tracking-wide font-medium mb-1" data-testid={`${testIdPrefix}-mortgage-amount-label`}>
                      Monthly Mortgage Payment Amount
                    </label>
                    <div className="text-[13px] font-bold text-[#2d3e50]" data-testid={`${testIdPrefix}-mortgage-amount-value`}>
                      {ad.rent_amount
                        ? formatAmount(ad.rent_amount)
                        : ""}
                    </div>
                  </div>
                )}
            </>
          ) : (
            <>
              {showApplicationAddressReadOnlyScalar(mode, ad.paid_rent) && (
                <div className="col-span-12 md:col-span-4 xl:col-span-3">
                  <label className="block text-[11px] text-[#5e6e82] uppercase tracking-wide font-medium mb-1" data-testid={`${testIdPrefix}-months-paid-rent-label`}>
                    Number of months you have paid rent at this address?
                  </label>
                  <div className="text-[13px] font-bold text-[#2d3e50]" data-testid={`${testIdPrefix}-months-paid-rent-value`}>
                    {ad.paid_rent}
                  </div>
                </div>
              )}
              {showApplicationAddressReadOnlyScalar(mode, ad.rent_amount) && (
                <div className="col-span-12 md:col-span-4 xl:col-span-2">
                  <label className="block text-[11px] text-[#5e6e82] uppercase tracking-wide font-medium mb-1" data-testid={`${testIdPrefix}-rent-amount-label`}>
                    Rent Amount
                  </label>
                  <div className="text-[13px] font-bold text-[#2d3e50]" data-testid={`${testIdPrefix}-rent-amount-value`}>
                    {ad.rent_amount
                      ? formatAmount(ad.rent_amount)
                      : ""}
                  </div>
                </div>
              )}
              {/* Angular `#addressView`: second column always shows this for current_address (same `rent_amount` as Rent Amount). */}
              {addressType === "current_address" && (
                <div className="col-span-12 md:col-span-4 xl:col-span-2">
                  <label className="block text-[11px] text-[#5e6e82] uppercase tracking-wide font-medium mb-1" data-testid={`${testIdPrefix}-mortgage-amount-label`}>
                    Monthly Mortgage Payment Amount
                  </label>
                  <div className="text-[13px] font-bold text-[#2d3e50]" data-testid={`${testIdPrefix}-mortgage-amount-value`}>
                    {ad.rent_amount ? formatAmount(ad.rent_amount) : ""}
                  </div>
                </div>
              )}
            </>
          )}

          {showMoveIn && (
            <div className="col-span-12 md:col-span-4 xl:col-span-2">
              <label className="block text-[11px] text-[#5e6e82] uppercase tracking-wide font-medium mb-1" data-testid={`${testIdPrefix}-move-in-label`}>
                Move-in
              </label>
              <div className="text-[15px] font-bold text-[#2d3e50]" data-testid={`${testIdPrefix}-move-in-value`}>
                {moveInDisplay}
              </div>
            </div>
          )}

          {showStillHere && (
            <div className="col-span-12 md:col-span-4 xl:col-span-2">
              <label className="block text-[11px] text-[#5e6e82] uppercase tracking-wide font-medium mb-1" data-testid={`${testIdPrefix}-still-here-label`}>
                Still here
              </label>
              <div className="text-[13px] font-bold text-[#2d3e50]" data-testid={`${testIdPrefix}-still-here-value`}>Yes</div>
            </div>
          )}

          {showPlannedMoveOut && (
            <div className="col-span-12 md:col-span-4 xl:col-span-2">
              <label className="block text-[11px] text-[#5e6e82] uppercase tracking-wide font-medium mb-1" data-testid={`${testIdPrefix}-planned-move-out-label`}>
                Planned Move-Out
              </label>
              <div className="text-[13px] font-bold text-[#2d3e50]" data-testid={`${testIdPrefix}-planned-move-out-value`}>
                {moveOutDisplay}
              </div>
            </div>
          )}
            </>
          )}
        </div>
      )}

      {showLandlordGrid && (
        <div className={`${styles.balancedAddressGrid} grid grid-cols-12 gap-6 mb-6`}>
          {showApplicationAddressReadOnlyScalar(mode, ad.landlord_name) && (
            <div className="col-span-12 md:col-span-4">
              <label className="block text-[11px] text-[#5e6e82] uppercase tracking-wide font-medium mb-1" data-testid={`${testIdPrefix}-landlord-name-label`}>
                {landlordPrefix} Name
              </label>
              <div className="text-[13px] font-bold text-[#2d3e50]" data-testid={`${testIdPrefix}-landlord-name-value`}>
                {ad.landlord_name}
              </div>
            </div>
          )}
          {showApplicationAddressReadOnlyScalar(mode, ad.landlord_email) && (
            <div className="col-span-12 md:col-span-4">
              <label className="block text-[11px] text-[#5e6e82] uppercase tracking-wide font-medium mb-1" data-testid={`${testIdPrefix}-landlord-email-label`}>
                {landlordPrefix} Email
              </label>
              <div className="text-[13px] font-bold text-[#2d3e50] mb-1" data-testid={`${testIdPrefix}-landlord-email-value`}>
                {ad.landlord_email}
              </div>
              {canValidateEmail && ad.landlord_email && (
                <button
                  type="button"
                  className={`${styles.btnSmall} ${styles.validateEmailButton}`}
                  data-testid={`${testIdPrefix}-validate-landlord-email-button`}
                onClick={() =>
                    onVerifyEmail?.(ad.landlord_email as string)
                  }
                  disabled={verifyingEmail}
                >
                  {verifyingEmail ? "Validating..." : "Validate Email"}
                </button>
              )}
            </div>
          )}
          {showApplicationAddressReadOnlyField(mode, phoneDisplay) && (
            <div className="col-span-12 md:col-span-4">
              <label className="block text-[11px] text-[#5e6e82] uppercase tracking-wide font-medium mb-1" data-testid={`${testIdPrefix}-landlord-phone-label`}>
                {landlordPrefix} Phone
              </label>
              <div className="text-[13px] font-bold text-[#2d3e50]" data-testid={`${testIdPrefix}-landlord-phone-value`}>
                {formatPhoneNumber(ad.landlord_mobile_no ?? "")}
              </div>
            </div>
          )}
          {addressData.address_type === "FAMILY" &&
            showApplicationAddressReadOnlyScalar(
              mode,
              ad.landlord_relationship_type,
            ) && (
              <div className="col-span-12 md:col-span-4">
                <label className="block text-[11px] text-[#5e6e82] uppercase tracking-wide font-medium mb-1" data-testid={`${testIdPrefix}-contact-relationship-label`}>
                  Contact Relationship
                </label>
                <div className="text-[13px] font-bold text-[#2d3e50]" data-testid={`${testIdPrefix}-contact-relationship-value`}>
                  {ad.landlord_relationship_type}
                </div>
              </div>
            )}
        </div>
      )}
    </div>
  );
}
